import type { Participant } from '@seen/core/models/patients/participant'
import { Button, cn } from '@seen/ui'
import { IconX } from '@tabler/icons-react'
import { type JSX, type ReactNode, useState } from 'react'
import { useLocation } from 'react-router'
import { ResizableHandle } from '#src/components/core/resizable/ResizableHandle.tsx'
import { ResizablePanel } from '#src/components/core/resizable/ResizablePanel.tsx'
import { ResizablePanelGroup } from '#src/components/core/resizable/ResizablePanelGroup.tsx'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import { ScrollAreaHeader } from '#src/components/core/scroll-area/ScrollAreaHeader.tsx'
import { Sheet } from '#src/components/core/sheet/Sheet.tsx'
import { SheetContent } from '#src/components/core/sheet/SheetContent.tsx'
import { SheetTrigger } from '#src/components/core/sheet/SheetTrigger.tsx'
import { PatientNavigation } from '#src/components/patient/page/PatientNavigation.tsx'
import { PatientNavigationButton } from '#src/components/patient/page/PatientNavigationButton.tsx'
import { PatientHeaderCard } from '#src/components/patient/PatientHeaderCard.tsx'
import { PatientMoreMenu } from '#src/components/patient/PatientMoreMenu.tsx'
import { LayoutModeContext } from '#src/contexts/LayoutModeContext.ts'
import type { PatientNavigationItem } from '#src/hooks/usePatientNavigationItems.ts'

export interface BasePageLayoutProps {
  entity: Participant
  navigationItems: PatientNavigationItem[]

  detail: ReactNode
  // Renders the participant detail content for the narrow-screen sheet. The
  // sheet's header card is passed through so the content can pin it (with the
  // tab bar) in its sticky blurred container.
  detailContent: (header: ReactNode) => ReactNode
  children: ReactNode
}

export function BasePageLayout({
  entity,
  navigationItems,
  detail,
  detailContent,
  children,
}: BasePageLayoutProps): JSX.Element {
  const [isDetailSheetOpen, setIsDetailSheetOpen] = useState(false)
  const { pathname } = useLocation()
  const [mode, setMode] = useState<'full' | 'auto'>('auto')

  /* The content `key` below remounts the page (and resets scroll) on every
   * pathname change. The notes views (`/notes/all`, `/notes/mine`,
   * `/notes/assessments`) are one page — a filtered view, not a navigation — so
   * collapse the view segment to keep their key stable; otherwise toggling
   * between them remounts the whole page and flashes its loading state. Other
   * routes are unaffected. */
  const contentKey = pathname.replace(
    /\/notes\/(all|mine|assessments)$/,
    '/notes'
  )

  // The Figma "Title Card (Full-Width/Narrow Screen)" — the wide header card
  // is the whole bar, with the nav menu in the card and a stretched overlay
  // trigger opening the facesheet sheet (node 201-2097). The open sheet
  // repeats the card in its stacked interactive form with the more menu and a
  // close button (node 201-2388).
  const nonLgHeaderContent = (
    <Sheet open={isDetailSheetOpen} onOpenChange={setIsDetailSheetOpen}>
      <div className="flex-1 p-2">
        <div className="relative">
          <PatientHeaderCard
            participant={entity}
            variant="wide"
            interactive={false}
            actions={
              // Above the stretched trigger so the menu stays clickable, and
              // outside it in the DOM so buttons don't nest. The nav button
              // borrows the more menu's vertical-margin trick so its 40px hit
              // target doesn't inflate the name row — Figma 201-2097 keeps
              // the details 2px below the name.
              <div className="relative z-10 -mr-3 flex items-center">
                <PatientNavigationButton
                  buttonClassName="-my-2.75 py-2.75"
                  navigationItems={navigationItems}
                />
              </div>
            }
          />
          <SheetTrigger asChild>
            <button
              type="button"
              aria-label="View participant details"
              className="absolute inset-0 rounded-lg hover:backdrop-brightness-hover"
            />
          </SheetTrigger>
        </div>
      </div>
      <SheetContent
        side="bottom"
        // The facesheet content owns its scrolling (per-tab scroll areas), so
        // skip the sheet's built-in ScrollArea; the single minmax(0,1fr) grid
        // row hands it a real height instead.
        disableScrollArea
        className="grid h-[calc(100dvh-2.5rem)] grid-rows-1 overflow-hidden rounded-t-lg border-t p-0"
        hideClose
      >
        {/* The header card rides inside the detail content's sticky blurred
            container (with the tab bar), so tab content scrolls behind it
            with the same veil as the regular view (Figma 201-2649). */}
        {detailContent(
          <PatientHeaderCard
            participant={entity}
            actions={
              <div className="-mr-3 flex items-center">
                <PatientMoreMenu participant={entity} />
                <Button
                  variant="ghost"
                  size="icon"
                  // Same vertical-margin trick as the more menu's trigger so
                  // the 40px hit target doesn't inflate the name row.
                  className="-my-2.75 py-2.75"
                  aria-label="Close participant details"
                  onClick={() => setIsDetailSheetOpen(false)}
                >
                  <IconX size={16} />
                </Button>
              </div>
            }
          />
        )}
      </SheetContent>
    </Sheet>
  )

  return (
    <ResizablePanelGroup direction="horizontal">
      <ResizablePanel
        id="page-layout-detail"
        defaultSize={25}
        className="hidden lg:block"
        order={1}
      >
        {detail}
      </ResizablePanel>
      <ResizableHandle className="hidden lg:block" />
      <ResizablePanel id="page-layout-main" defaultSize={75} order={2}>
        {mode === 'auto' && (
          <ScrollArea
            key={contentKey}
            scrollRestorationId={`page-layout-content-${entity.id}`}
          >
            <ScrollAreaHeader className="hidden p-0 lg:block">
              <PatientNavigation navigationItems={navigationItems} />
            </ScrollAreaHeader>
            <ScrollAreaHeader className="gap-0 p-0 lg:hidden print:hidden">
              {nonLgHeaderContent}
            </ScrollAreaHeader>
            <div
              className={cn(
                'flex flex-col gap-3 px-pagex-default pt-2.5 lg:px-pagex-lg lg:pt-2 print:pt-0',
                'flex-1',
                'pb-16 print:pb-0',
                'print:px-px' // Leave room for bordered elements (i.e. Card) that have been bumped out a pixel (so that their content is aligned with non-bordered elements).
              )}
            >
              <LayoutModeContext.Provider value={setMode}>
                {children}
              </LayoutModeContext.Provider>
            </div>
          </ScrollArea>
        )}
        {mode === 'full' && (
          <div className="flex h-full flex-col">
            <div className="hidden lg:block">
              <PatientNavigation navigationItems={navigationItems} />
            </div>
            <div className="flex lg:hidden print:hidden">
              {nonLgHeaderContent}
            </div>
            <div className="min-h-0 flex-1">
              <LayoutModeContext.Provider value={setMode}>
                {children}
              </LayoutModeContext.Provider>
            </div>
          </div>
        )}
      </ResizablePanel>
    </ResizablePanelGroup>
  )
}
